"use client";

import { c as _c } from "react/compiler-runtime";
import type { CommunityMemberMinimal } from "@/entities/IProfile";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { areEqualAddresses } from "@/helpers/Helpers";
import { getUserProfile } from "@/helpers/server.helpers";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useIdentity } from "@/hooks/useIdentity";
import { commonApiFetch } from "@/services/api/common-api";
import { faAnglesDown, faAnglesUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { isAddress } from "viem";
import TransferModalPfp from "../nft-transfer/TransferModalPfp";
const MIN_SEARCH_LENGTH = 3;
function getSearchStatusText(isSearching: boolean, needsMoreSearchCharacters: boolean, remainingSearchCharacters: number, resultsLen: number, trimmedQuery: string): string {
  if (isSearching) return "Searching…";
  if (needsMoreSearchCharacters) {
    const plural = remainingSearchCharacters === 1 ? "" : "s";
    return `Type ${remainingSearchCharacters} more character${plural}`;
  }
  if (resultsLen > 0) {
    const plural = resultsLen === 1 ? "" : "s";
    return `Found ${resultsLen} result${plural}`;
  }
  if (trimmedQuery) return "No results.";
  return "Type to search.";
}
function RecipientSelectedDisplay(t0) {
  const $ = _c(40);
  const {
    selectedProfile,
    profile,
    isIdentityLoading,
    onClear,
    allowProfileChange,
    showSelectedProfileCard,
    walletsListRef,
    walletsHasOverflow,
    walletsAtEnd,
    selectedWallet,
    onWalletSelect,
    disableSingleWalletSelection
  } = t0;
  let t1;
  if ($[0] !== profile || $[1] !== selectedProfile) {
    t1 = () => {
      if (profile?.wallets && profile.wallets.length > 0) {
        return profile.wallets;
      }
      if (selectedProfile.wallet) {
        return [{
          wallet: selectedProfile.wallet,
          display: selectedProfile.display || selectedProfile.handle || selectedProfile.wallet,
          tdh: 0
        }];
      }
      return [];
    };
    $[0] = profile;
    $[1] = selectedProfile;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const getWallets = t1;
  let wallets;
  let walletsContent;
  if ($[3] !== disableSingleWalletSelection || $[4] !== getWallets || $[5] !== isIdentityLoading || $[6] !== onWalletSelect || $[7] !== selectedProfile || $[8] !== selectedWallet) {
    wallets = getWallets();
    if (isIdentityLoading) {
      let t2;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = <div className="tw-text-xs tw-opacity-60">Loading wallets…</div>;
        $[11] = t2;
      } else {
        t2 = $[11];
      }
      walletsContent = t2;
    } else {
      if (wallets.length === 0) {
        const t2 = selectedProfile.display || selectedProfile.handle;
        let t3;
        if ($[12] !== t2) {
          t3 = <div className="tw-text-xs tw-opacity-60">No wallets found for {t2}.</div>;
          $[12] = t2;
          $[13] = t3;
        } else {
          t3 = $[13];
        }
        walletsContent = t3;
      } else {
        const hasSingleWallet = wallets.length === 1;
        const isWalletSelectionDisabled = disableSingleWalletSelection && hasSingleWallet;
        let t2;
        if ($[14] !== isWalletSelectionDisabled || $[15] !== onWalletSelect || $[16] !== selectedWallet) {
          t2 = w => {
            const isSel = selectedWallet?.toLowerCase() === w.wallet.toLowerCase();
            const hasDisplay = w.display && w.display.toLowerCase() !== w.wallet.toLowerCase();
            const classes = ["tw-flex tw-min-h-[58px] tw-w-full tw-flex-col tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900/50 tw-p-3 tw-transition-all", hasDisplay ? "tw-items-start tw-justify-between" : "tw-items-start tw-justify-center", isSel ? "!tw-border-primary-500 tw-bg-primary-500/10" : "tw-border-transparent"].join(" ");
            if (isWalletSelectionDisabled) {
              return <div key={w.wallet} className={classes}><div className="tw-text-sm tw-font-bold tw-text-white">{w.display || w.wallet}</div>{hasDisplay && <div className="tw-text-[11px] tw-font-medium tw-text-iron-500">{w.wallet}</div>}</div>;
            }
            return <button key={w.wallet} type="button" onClick={() => onWalletSelect(w.wallet)} className={[classes, "hover:tw-bg-iron-800"].join(" ")}><div className="tw-text-sm tw-font-bold tw-text-white">{w.display || w.wallet}</div>{hasDisplay && <div className="tw-text-[11px] tw-font-medium tw-text-iron-500">{w.wallet}</div>}</button>;
          };
          $[14] = isWalletSelectionDisabled;
          $[15] = onWalletSelect;
          $[16] = selectedWallet;
          $[17] = t2;
        } else {
          t2 = $[17];
        }
        walletsContent = wallets.map(t2);
      }
    }
    $[3] = disableSingleWalletSelection;
    $[4] = getWallets;
    $[5] = isIdentityLoading;
    $[6] = onWalletSelect;
    $[7] = selectedProfile;
    $[8] = selectedWallet;
    $[9] = wallets;
    $[10] = walletsContent;
  } else {
    wallets = $[9];
    walletsContent = $[10];
  }
  let t2;
  if ($[18] !== allowProfileChange || $[19] !== onClear || $[20] !== selectedProfile || $[21] !== showSelectedProfileCard) {
    t2 = showSelectedProfileCard && <div className="tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900/50 tw-px-4 tw-py-3"><div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3"><TransferModalPfp src={selectedProfile.pfp} alt={selectedProfile.display || selectedProfile.handle || selectedProfile.wallet} level={selectedProfile.level} /><div className="tw-min-w-0"><div className="tw-truncate tw-text-sm tw-font-bold tw-text-white">{selectedProfile.handle || selectedProfile.display}</div><div className="tw-truncate tw-text-[11px] tw-font-medium tw-text-iron-500">TDH: {selectedProfile.tdh.toLocaleString()} — Level:{" "}{selectedProfile.level}</div></div></div>{allowProfileChange && <button type="button" className="tw-rounded-lg tw-border-0 tw-bg-iron-800 tw-px-3 tw-py-1.5 tw-text-xs tw-font-bold tw-text-iron-200 tw-transition-colors hover:tw-bg-iron-700" onClick={onClear}>Change</button>}</div>;
    $[18] = allowProfileChange;
    $[19] = onClear;
    $[20] = selectedProfile;
    $[21] = showSelectedProfileCard;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  const t3 = `tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-space-y-3 ${showSelectedProfileCard ? "tw-pt-6" : "tw-pt-0"}`;
  let t4;
  if ($[23] !== showSelectedProfileCard || $[24] !== wallets.length) {
    t4 = (showSelectedProfileCard ? wallets.length > 1 : wallets.length > 0) && <div className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-iron-400">Choose destination wallet</div>;
    $[23] = showSelectedProfileCard;
    $[24] = wallets.length;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] !== walletsContent || $[27] !== walletsListRef) {
    t5 = <div ref={walletsListRef} className="tw-[scrollbar-gutter:stable] tw-min-h-0 tw-flex-1 tw-space-y-2 tw-overflow-auto tw-pr-3 tw-scrollbar-thin tw-scrollbar-track-transparent tw-scrollbar-thumb-white/10 hover:tw-scrollbar-thumb-white/20">{walletsContent}</div>;
    $[26] = walletsContent;
    $[27] = walletsListRef;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== walletsAtEnd || $[30] !== walletsHasOverflow) {
    t6 = walletsHasOverflow && <div className="tw-text-center tw-text-xs tw-text-iron-500"><FontAwesomeIcon icon={walletsAtEnd ? faAnglesUp : faAnglesDown} />{" "}Scroll for more</div>;
    $[29] = walletsAtEnd;
    $[30] = walletsHasOverflow;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== t3 || $[33] !== t4 || $[34] !== t5 || $[35] !== t6) {
    t7 = <div className={t3}>{t4}{t5}{t6}</div>;
    $[32] = t3;
    $[33] = t4;
    $[34] = t5;
    $[35] = t6;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  let t8;
  if ($[37] !== t2 || $[38] !== t7) {
    t8 = <>{t2}{t7}</>;
    $[37] = t2;
    $[38] = t7;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  return t8;
}
function RecipientSearchDisplay(t0) {
  const $ = _c(25);
  const {
    query,
    setQuery,
    searchStatusText,
    results,
    onPick,
    resultsListRef,
    resultsHasOverflow,
    resultsAtEnd,
    searchInputRef,
    placeholder
  } = t0;
  let t1;
  if ($[0] !== setQuery) {
    t1 = e => setQuery(e.target.value);
    $[0] = setQuery;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = placeholder ?? "Search profile by handle, ens or wallet";
  let t3;
  if ($[2] !== query || $[3] !== searchInputRef || $[4] !== t1 || $[5] !== t2) {
    t3 = <div className="tw-relative"><input autoFocus={true} type="text" value={query} onChange={t1} placeholder={t2} className="tw-h-12 tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-placeholder-iron-500 tw-transition-all focus:tw-border-primary-500 focus:tw-bg-iron-950 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary-500" ref={searchInputRef} /></div>;
    $[2] = query;
    $[3] = searchInputRef;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== searchStatusText) {
    t4 = <div className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-iron-500">{searchStatusText}</div>;
    $[7] = searchStatusText;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onPick || $[10] !== results) {
    let t6;
    if ($[12] !== onPick) {
      t6 = r => <button key={r.profile_id ?? r.wallet} type="button" onClick={() => onPick(r)} className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-transparent tw-bg-iron-900/50 tw-p-3 tw-text-left tw-transition-all hover:tw-border-iron-700 hover:tw-bg-iron-800"><TransferModalPfp src={r.pfp} alt={r.display || r.handle || r.wallet} level={r.level} /><div className="tw-min-w-0 tw-flex-1"><div className="tw-truncate tw-text-sm tw-font-bold tw-text-white">{r.handle || r.display}</div><div className="tw-truncate tw-text-[11px] tw-font-medium tw-text-iron-500">TDH: {r.tdh.toLocaleString()} — Level: {r.level}</div></div></button>;
      $[12] = onPick;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t5 = results.map(t6);
    $[9] = onPick;
    $[10] = results;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[14] !== resultsListRef || $[15] !== t5) {
    t6 = <div ref={resultsListRef} className="tw-[scrollbar-gutter:stable] tw-min-h-0 tw-flex-1 tw-space-y-2 tw-overflow-auto tw-pr-3 tw-scrollbar-thin tw-scrollbar-track-transparent tw-scrollbar-thumb-white/10 hover:tw-scrollbar-thumb-white/20">{t5}</div>;
    $[14] = resultsListRef;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== resultsAtEnd || $[18] !== resultsHasOverflow) {
    t7 = resultsHasOverflow && <div className="tw-text-center tw-text-xs tw-text-iron-500"><FontAwesomeIcon icon={resultsAtEnd ? faAnglesUp : faAnglesDown} />{" "}Scroll for more</div>;
    $[17] = resultsAtEnd;
    $[18] = resultsHasOverflow;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t6 || $[23] !== t7) {
    t8 = <>{t3}{t4}{t6}{t7}</>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}
interface RecipientSelectorProps {
  readonly open: boolean;
  readonly selectedProfile: CommunityMemberMinimal | null;
  readonly selectedWallet: string | null;
  readonly onProfileSelect: (profile: CommunityMemberMinimal | null) => void;
  readonly onWalletSelect: (wallet: string | null) => void;
  readonly placeholder?: string;
  readonly showLabel?: boolean;
  readonly label?: string;
  readonly allowProfileChange?: boolean;
  readonly disableSingleWalletSelection?: boolean;
  readonly showSelectedProfileCard?: boolean;
}
export default function RecipientSelector({
  open,
  selectedProfile,
  selectedWallet,
  onProfileSelect,
  onWalletSelect,
  placeholder,
  showLabel = true,
  label = "Recipient",
  allowProfileChange = true,
  disableSingleWalletSelection = false,
  showSelectedProfileCard = true
}: RecipientSelectorProps) {
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<CommunityMemberMinimal[]>([]);
  const [pendingWalletMatch, setPendingWalletMatch] = useState<string | null>(null);
  const pendingMatchAttempted = useRef(false);
  const handleOrWallet = selectedProfile?.handle ?? selectedProfile?.wallet ?? null;
  const {
    profile,
    isLoading: isIdentityLoading
  } = useIdentity({
    handleOrWallet: handleOrWallet ?? "",
    initialProfile: null
  });
  const resultsListRef = useRef<HTMLDivElement | null>(null);
  const walletsListRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [resultsHasOverflow, setResultsHasOverflow] = useState(false);
  const [walletsHasOverflow, setWalletsHasOverflow] = useState(false);
  const [resultsAtEnd, setResultsAtEnd] = useState(false);
  const [walletsAtEnd, setWalletsAtEnd] = useState(false);
  const trimmedQuery = query.trim();
  const needsMoreSearchCharacters = trimmedQuery.length > 0 && trimmedQuery.length < MIN_SEARCH_LENGTH;
  const remainingSearchCharacters = Math.max(MIN_SEARCH_LENGTH - trimmedQuery.length, 0);
  useEffect(() => {
    if (!open) {
      pendingMatchAttempted.current = false;
      setQuery("");
      setIsSearching(false);
      setResults([]);
      setResultsHasOverflow(false);
      setWalletsHasOverflow(false);
      setResultsAtEnd(false);
      setWalletsAtEnd(false);
      setPendingWalletMatch(null);
    }
  }, [open]);
  useEffect(() => {
    if (!selectedProfile || isIdentityLoading || !profile?.wallets || selectedWallet) {
      return;
    }
    const wallets = profile.wallets;
    if (pendingWalletMatch) {
      const normalizedMatch = pendingWalletMatch.toLowerCase();
      const matchedWallet = wallets.find(w => w.wallet.toLowerCase() === normalizedMatch || w.display?.toLowerCase() === normalizedMatch);
      if (matchedWallet) {
        onWalletSelect(matchedWallet.wallet);
      }
      setPendingWalletMatch(null);
      return;
    }
    if (wallets.length === 1 && wallets[0] && !pendingMatchAttempted.current) {
      onWalletSelect(wallets[0].wallet);
    }
  }, [selectedProfile, isIdentityLoading, profile?.wallets, selectedWallet, pendingWalletMatch, onWalletSelect]);
  const debouncedQuery = useDebouncedValue(trimmedQuery, 350);
  const enabled = open && debouncedQuery.length >= MIN_SEARCH_LENGTH;
  const {
    data,
    isFetching
  } = useQuery<CommunityMemberMinimal[]>({
    queryKey: ["memberSearch", debouncedQuery],
    enabled,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 30_000,
    gcTime: 300_000,
    queryFn: async ({
      signal
    }: {
      signal: AbortSignal;
    }) => {
      try {
        const arr = await commonApiFetch<CommunityMemberMinimal[]>({
          endpoint: "community-members",
          params: {
            param: debouncedQuery
          },
          signal
        });
        if (arr && arr.length > 0) return arr;
        const identity = await getUserProfile({
          user: debouncedQuery,
          headers: {}
        });
        const communityMember: CommunityMemberMinimal = {
          profile_id: identity.id,
          handle: identity.handle,
          wallet: identity.primary_wallet,
          pfp: identity.pfp,
          display: identity.display ?? null,
          normalised_handle: identity.normalised_handle,
          primary_wallet: identity.primary_wallet,
          tdh: identity.tdh,
          level: identity.level,
          cic_rating: identity.cic
        };
        return [communityMember];
      } catch {
        return [];
      }
    }
  });
  useEffect(() => {
    const preDebounce = open && trimmedQuery.length >= MIN_SEARCH_LENGTH && debouncedQuery !== trimmedQuery;
    setIsSearching(preDebounce || enabled && isFetching);
  }, [open, trimmedQuery, debouncedQuery, enabled, isFetching]);
  useEffect(() => {
    if (!enabled) {
      setResults([]);
      return;
    }
    setResults(data ?? []);
  }, [enabled, data, setResults]);
  useEffect(() => {
    if (selectedProfile || !data || data.length === 0 || !debouncedQuery || !trimmedQuery) {
      return;
    }
    const normalizedQuery = debouncedQuery.toLowerCase();
    const isExactAddress = isAddress(debouncedQuery);
    const isLikelyEns = normalizedQuery.endsWith(".eth");
    if (!isExactAddress && !isLikelyEns) {
      return;
    }
    if (data.length === 1 && data[0]) {
      const result = data[0];
      const walletMatch = areEqualAddresses(result.wallet, debouncedQuery);
      if (isExactAddress ? walletMatch : isLikelyEns) {
        pendingMatchAttempted.current = true;
        setPendingWalletMatch(debouncedQuery);
        onProfileSelect(result);
      }
    }
  }, [data, debouncedQuery, trimmedQuery, selectedProfile, onProfileSelect]);
  useEffect(() => {
    const hasOverflow = (el: HTMLElement | null) => !!el && el.scrollHeight > el.clientHeight;
    const check = () => {
      setResultsHasOverflow(hasOverflow(resultsListRef.current));
      setWalletsHasOverflow(hasOverflow(walletsListRef.current));
      const nearBottom = (el_0: HTMLElement | null) => {
        if (!el_0) return false;
        const threshold = 4;
        return el_0.scrollTop + el_0.clientHeight >= el_0.scrollHeight - threshold;
      };
      setResultsAtEnd(nearBottom(resultsListRef.current));
      setWalletsAtEnd(nearBottom(walletsListRef.current));
    };
    check();
    globalThis.addEventListener("resize", check);
    const resultsEl = resultsListRef.current;
    const walletsEl = walletsListRef.current;
    const onScrollResults = () => {
      const el_1 = resultsListRef.current;
      if (!el_1) return;
      const threshold_0 = 4;
      setResultsAtEnd(el_1.scrollTop + el_1.clientHeight >= el_1.scrollHeight - threshold_0);
    };
    const onScrollWallets = () => {
      const el_2 = walletsListRef.current;
      if (!el_2) return;
      const threshold_1 = 4;
      setWalletsAtEnd(el_2.scrollTop + el_2.clientHeight >= el_2.scrollHeight - threshold_1);
    };
    resultsEl?.addEventListener("scroll", onScrollResults);
    walletsEl?.addEventListener("scroll", onScrollWallets);
    return () => {
      globalThis.removeEventListener("resize", check);
      resultsEl?.removeEventListener("scroll", onScrollResults);
      walletsEl?.removeEventListener("scroll", onScrollWallets);
    };
  }, [open, results, profile?.wallets, isIdentityLoading]);
  const searchStatusText = getSearchStatusText(isSearching, needsMoreSearchCharacters, remainingSearchCharacters, results.length, trimmedQuery);
  const handleClear = () => {
    pendingMatchAttempted.current = false;
    setPendingWalletMatch(null);
    onProfileSelect(null);
    onWalletSelect(null);
    setResults([]);
    setQuery("");
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };
  return <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-space-y-3">
      {showLabel && <div className={`tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-iron-400 ${selectedProfile ? "tw-mb-2" : "tw-mb-0"}`}>
          {label}
        </div>}
      {selectedProfile ? <RecipientSelectedDisplay selectedProfile={selectedProfile} profile={profile} isIdentityLoading={isIdentityLoading} onClear={handleClear} allowProfileChange={allowProfileChange} showSelectedProfileCard={showSelectedProfileCard} walletsListRef={walletsListRef} walletsHasOverflow={walletsHasOverflow} walletsAtEnd={walletsAtEnd} selectedWallet={selectedWallet} onWalletSelect={onWalletSelect} disableSingleWalletSelection={disableSingleWalletSelection} /> : <RecipientSearchDisplay query={query} setQuery={setQuery} searchStatusText={searchStatusText} results={results} onPick={r => {
      onProfileSelect(r);
      onWalletSelect(null);
    }} resultsListRef={resultsListRef} resultsHasOverflow={resultsHasOverflow} resultsAtEnd={resultsAtEnd} searchInputRef={searchInputRef} placeholder={placeholder ?? "Search profile by handle, ens or wallet"} />}
    </div>;
}